/*
    Copyright (C) 2017 Red Hat, Inc.

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

            http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { ConfigModel } from '../../models/config.model';
import { Field } from '../../models/field.model';
import { MappingModel } from '../../models/mapping.model';

import { ModalWindowComponent } from '../modal-window.component';
import { MappingSelectionComponent } from './mapping-selection.component';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'mapping-detail',
  template: `<div class='fieldMappingDetail' *ngIf="cfg.mappings.activeMapping && cfg.showMappingDetailTray">
  <div class="card-pf">
    <div class="card-pf-heading">
      <h2 class="card-pf-title clearfix">
        <div class="pull-left">{{ getTitle() }}</div>
        <i class="fa fa-trash link pull-right" aria-hidden="true" (click)="removeMapping($event)" tooltip="Remove the current mapping" placement="left"></i>
      </h2>
    </div>
    <div class="fieldMappingDetail-body">
      <div class="alert alert-danger" *ngFor="let error of cfg.mappings.activeMapping.getValidationErrors()">
        <a class="close" (click)="cfg.mappings.activeMapping.removeError(error.identifier)">
          <i class="fa fa-close"></i>
        </a>
        <span class="pficon pficon-error-circle-o"></span>
        <label>{{ error.message }}</label>
      </div>
      <div class="alert alert-warning" *ngFor="let warn of cfg.mappings.activeMapping.getValidationWarnings()">
        <a class="close" (click)="cfg.mappings.activeMapping.removeError(warn.identifier)">
          <i class="fa fa-close"></i>
        </a>
        <span class="pficon pficon-warning-triangle-o"></span>
        <label>{{ warn.message }}</label>
      </div>
      <div *ngIf="!isMappingCollection()">
        <mapping-pair-detail *ngFor="let fieldPair of cfg.mappings.activeMapping.fieldMappings" [cfg]="cfg" [fieldPair]="fieldPair"
          [modalWindow]="modalWindow"></mapping-pair-detail>
      </div>
      <collection-mapping [cfg]="cfg" *ngIf="isMappingCollection()"></collection-mapping>
    </div>
  </div>
</div>`,
})

export class MappingDetailComponent implements OnInit, OnDestroy {
  @Input() cfg: ConfigModel;
  @Input() modalWindow: ModalWindowComponent;

  private mappingSelectionRequiredSubscription: Subscription;

  ngOnInit(): void {
    this.mappingSelectionRequiredSubscription = this.cfg.mappingService.mappingSelectionRequired$.subscribe((field: Field) => {
      this.selectMapping(field);
    });
  }

  ngOnDestroy(): void {
    this.mappingSelectionRequiredSubscription.unsubscribe();
  }

  isMappingCollection(): boolean {
    return this.cfg.mappings.activeMapping.isCollectionMode();
  }

  getTitle(): string {
    if (this.cfg.mappings.activeMapping.isLookupMode()) {
      return 'Lookup Mapping';
    }
    return this.isMappingCollection() ? 'Repeating Mapping' : 'Mapping Details';
  }

  removeMapping(event: MouseEvent): void {
    this.modalWindow.reset();
    this.modalWindow.confirmButtonText = 'Remove';
    this.modalWindow.headerText = 'Remove Mapping?';
    this.modalWindow.message = 'Are you sure you want to remove the current mapping?';
    this.modalWindow.okButtonHandler = (mw: ModalWindowComponent) => {
      this.cfg.mappingService.removeMapping(this.cfg.mappings.activeMapping);
      this.cfg.showMappingDetailTray = false;
    };
    this.modalWindow.show();
  }

  private selectMapping(field: Field): void {
    const mappingsForField: MappingModel[] = this.cfg.mappings.findMappingsForField(field);
    const self: MappingDetailComponent = this;
    this.modalWindow.reset();
    this.modalWindow.confirmButtonText = 'Select';
    this.modalWindow.headerText = 'Select Mapping';
    this.modalWindow.nestedComponentInitializedCallback = (mw: ModalWindowComponent) => {
      const c: MappingSelectionComponent = mw.nestedComponent as MappingSelectionComponent;
      c.selectedField = field;
      c.cfg = self.cfg;
      c.mappings = mappingsForField;
      c.modalWindow = this.modalWindow;
    };
    this.modalWindow.nestedComponentType = MappingSelectionComponent;
    this.modalWindow.okButtonHandler = (mw: ModalWindowComponent) => {
      const c: MappingSelectionComponent = mw.nestedComponent as MappingSelectionComponent;
      const mapping: MappingModel = c.getSelectedMapping();
      self.cfg.mappingService.selectMapping(mapping);
    };
    this.modalWindow.cancelButtonHandler = (mw: ModalWindowComponent) => {
      self.cfg.mappingService.selectMapping(null);
    };
    this.modalWindow.show();
  }
}
